/**
 * V2WorkspacePage — production workspace, auth-protected.
 *
 * Renders the full workspace UI (same components as V2WorkspacePreview)
 * but wired to real API data instead of mock data.
 *
 * Routes:
 *   /v2/workspace        → renders with no shipment selected
 *   /v2/workspace/:id    → renders with the URL-pinned shipment active
 */

import { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useV2Api } from '../../../hooks/useV2Api'
import { useShipmentActivityStream } from '../../../hooks/useShipmentActivityStream'

// Preview UI components — reused with real data
import Sidebar from './preview/Sidebar'
import Header from './preview/Header'
import ActivityLog, { ProviderSelectionPanel, InputBar } from './preview/ActivityLog'
import ShipmentRail from './preview/ShipmentRail'
import WorkspaceChatIntake from './WorkspaceChatIntake'
import { adaptActivityItem } from './preview/activityAdapter'
import { adaptShipment } from './shipmentAdapter'
import fetchAllShipments from './fetchAllShipments'
import { buildChatDraftShipment } from './chatDraftShipment'
import { friendlyErrorMessage } from '../../../utils/friendlyError'
import './V2Workspace.css'

function QuoteRequestSentPanel({ confirmation, onSendMore }) {
  const providerCount = confirmation?.providerCount || 0
  return (
    <div className="ws-rfq-success-panel" role="status" aria-live="polite">
      <div className="ws-rfq-success-copy">
        <div className="ws-rfq-success-eyebrow">RFQs sent</div>
        <div className="ws-rfq-success-title">Quote requests sent</div>
        <div className="ws-rfq-success-text">
          Sent to {providerCount} provider{providerCount === 1 ? '' : 's'}. Replies will appear in this workspace as they come in.
        </div>
      </div>
      <div className="ws-rfq-success-actions">
        <button className="ws-btn ws-btn--outline" onClick={onSendMore}>
          Send to more providers
        </button>
      </div>
    </div>
  )
}

function QuoteAcceptedBanner({ confirmation, onDismiss }) {
  // Auto-dismiss success toasts after 4s. Errors are not auto-dismissed
  // elsewhere because the user needs time to read and act; successes are
  // transient confirmations and don't need to linger.
  useEffect(() => {
    if (!confirmation) return undefined
    const timer = setTimeout(() => onDismiss?.(), 4000)
    return () => clearTimeout(timer)
  }, [confirmation, onDismiss])
  if (!confirmation) return null
  return (
    <div className="ws-toast ws-toast--success" role="status" aria-live="polite">
      <span className="ws-toast-text">
        Quote accepted{confirmation.provider ? ` from ${confirmation.provider}` : ''}. Booking intent email sent to the provider.
      </span>
      <button className="ws-toast-dismiss" onClick={onDismiss} aria-label="Dismiss confirmation">×</button>
    </div>
  )
}

const quoteIdOf = (quote) => quote?.id || quote?.quoteId || quote?.quote_id

function mergeShipmentQuoteStateIntoActivity(items, shipmentDetail) {
  if (!shipmentDetail || !Array.isArray(items)) return items

  const quotes = Array.isArray(shipmentDetail.quotes) ? shipmentDetail.quotes : []
  const selectedQuoteId = shipmentDetail.selected_quote_id || shipmentDetail.selectedQuoteId || quoteIdOf(quotes.find(q => q.selected))
  const quoteById = new Map(quotes.map(q => [quoteIdOf(q), q]).filter(([id]) => !!id))

  if (!selectedQuoteId && quoteById.size === 0) return items

  return items.map(item => {
    if (item.kind !== 'quote' || !item.quoteId) return item
    const quote = quoteById.get(item.quoteId)
    const isSelected = selectedQuoteId && item.quoteId === selectedQuoteId
    const isNotSelectedAfterBooking = selectedQuoteId && item.quoteId !== selectedQuoteId
    const quoteStatus = quote?.status || (isSelected ? 'SELECTED' : isNotSelectedAfterBooking ? 'REJECTED' : item.status)

    return {
      ...item,
      status: quoteStatus,
      selected: quote?.selected === true || isSelected,
      selectionReason: quote?.selection_reason || quote?.selectionReason || item.selectionReason || null
    }
  })
}

// ────────────────────────────────────────────────────────────────────────────
// Page component
// ────────────────────────────────────────────────────────────────────────────

export default function V2WorkspacePage() {
  const { id: urlShipmentId } = useParams()
  const navigate = useNavigate()
  const api = useV2Api()

  const [shipments, setShipments] = useState([])
  const [chatSessions, setChatSessions] = useState([])
  const [activeId, setActiveId] = useState(urlShipmentId || null)
  const [activityByShipment, setActivityByShipment] = useState({})
  const [filter, setFilter] = useState('All')
  const [input, setInput] = useState('')
  // Detail panel is a collapsible drawer (mirrors V2WorkspacePreview): open by
  // default so behaviour is unchanged, collapsible to a thin tab to give the
  // chat room.
  const [railOpen, setRailOpen] = useState(true)
  const [loading, setLoading] = useState(true)
  const [activityLoading, setActivityLoading] = useState(false)
  const [error, setError] = useState(null)
  const [chatSessionId, setChatSessionId] = useState(null)
  const [chatDraftState, setChatDraftState] = useState({})
  const [quoteSendConfirmations, setQuoteSendConfirmations] = useState({})
  const [sendingQuoteSelectionId, setSendingQuoteSelectionId] = useState(null)
  const [acceptingQuoteId, setAcceptingQuoteId] = useState(null)
  const [viewingPdfQuoteId, setViewingPdfQuoteId] = useState(null)
  const [quoteAcceptConfirmation, setQuoteAcceptConfirmation] = useState(null)
  // Concurrency guard for the chat input — InputBar's Enter handler fires
  // synchronously, so without this flag a fast double-press queues two POSTs
  // before React re-renders. The ref backs the guard inside handleSend (no
  // stale closure); the state copy drives the InputBar `disabled` prop.
  const [isSending, setIsSending] = useState(false)
  const isSendingRef = useRef(false)
  // Tracks which shipment is awaiting Marq's reply. Set on send, cleared when
  // the SSE stream delivers a fresh assistant message. Surfaced as a "Marq is
  // thinking…" indicator below the last user bubble so the wait isn't silent.
  const [thinkingShipmentId, setThinkingShipmentId] = useState(null)

  // Sync URL → activeId
  useEffect(() => {
    if (urlShipmentId && urlShipmentId !== activeId) {
      setActiveId(urlShipmentId)
    }
  }, [urlShipmentId, activeId])

  // Merge new activity items with existing ones by id. Preserves the object
  // reference for items that haven't changed, so React.memo'd children and
  // their internal state (scroll position, checked candidates, transient
  // animation) survive a refetch. Without this, every refetch swaps every
  // item's reference and the entire ActivityLog re-renders from scratch.
  const mergeActivity = (existing, fresh) => {
    if (!existing || existing.length === 0) return fresh
    const existingById = new Map(existing.map(i => [i.id, i]))
    return fresh.map(item => {
      const prev = existingById.get(item.id)
      if (!prev) return item
      // Shallow compare known dynamic fields; fall back to identity if equal.
      const same = prev.kind === item.kind
        && prev.role === item.role
        && prev.text === item.text
        && prev.dismissed === item.dismissed
        && JSON.stringify(prev.payload || null) === JSON.stringify(item.payload || null)
      return same ? prev : item
    })
  }

  // Load shipment list + active chat sessions on mount
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    Promise.all([
      fetchAllShipments(api).catch(() => ({ data: [] })),
      api.listChatSessions().catch(() => ({ data: [] }))
    ]).then(([shipRes, sessRes]) => {
      if (cancelled) return
      const list = shipRes?.data || []
      const sessions = (sessRes?.data || []).filter(s => s.status === 'active')
      setShipments(list)
      setChatSessions(sessions)

      if (activeId) return // URL has a shipment ID — use it

      // Resume most recent active chat session if any
      if (sessions.length > 0) {
        setChatSessionId(sessions[0].id)
        return
      }

      // Otherwise select the first shipment
      if (list.length > 0) {
        navigate(`/workspace/${list[0].id}`, { replace: true })
      }
    })
      .catch(err => !cancelled && setError(err || new Error('Failed to load')))
      .finally(() => !cancelled && setLoading(false))
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Hydrate the active shipment with its quotes+events. The list endpoint
  // (getAll) returns shallow rows without `quotes`, so the right-rail's
  // Providers section would otherwise read shipment.providers = [] and show
  // "No providers contacted yet" even after carriers have replied. Pulling
  // the full record via GET /:id and merging back into `shipments` gives the
  // adapter the quote rows it needs to derive providers.
  useEffect(() => {
    if (!activeId || typeof api.getShipment !== 'function') return
    const controller = new AbortController()
    api.getShipment(activeId, { signal: controller.signal })
      .then(res => {
        if (controller.signal.aborted) return
        const full = res?.data
        if (!full || !full.id) return
        setShipments(prev => {
          const idx = prev.findIndex(s => s.id === full.id)
          if (idx === -1) return [full, ...prev]
          const next = prev.slice()
          next[idx] = { ...prev[idx], ...full }
          return next
        })
      })
      .catch(err => {
        if (err?.name === 'AbortError' || controller.signal.aborted) return
        /* non-fatal: rail just stays on the shallow row */
      })
    return () => controller.abort()
  }, [activeId, api])

  // Load activity for active shipment.
  // Refetch-aggressive: switching shipments must always show fresh data. We
  // clear the cached items for the entered shipment, force the skeleton, and
  // abort any in-flight fetch from the previous selection. This trades a tiny
  // skeleton flash on revisit for the guarantee that fast A→B→A switching
  // never leaks stale rows or hangs on a stale-fetch's `loading=false`.
  useEffect(() => {
    if (!activeId) return
    const controller = new AbortController()
    const requestedId = activeId
    setActivityLoading(true)
    setActivityByShipment(prev => {
      if (!prev[requestedId] || prev[requestedId].length === 0) return prev
      return { ...prev, [requestedId]: [] }
    })
    api.getShipmentActivity(activeId, { signal: controller.signal })
      .then(res => {
        if (controller.signal.aborted) return
        const items = (res?.data || res || []).map(adaptActivityItem)
        // Preserve any SSE items that arrived while this fetch was in flight.
        // Without this guard, an SSE quote_card that lands before the GET
        // resolves gets wiped when we replace the array, and the SSE merge
        // effect below won't re-add it because prevStreamLenRef already
        // advanced past it.
        setActivityByShipment(prev => {
          const existing = prev[requestedId] || []
          const incomingIds = new Set(items.map(i => i.id))
          const ssePending = existing.filter(i => !incomingIds.has(i.id))
          return {
            ...prev,
            [requestedId]: [...items, ...ssePending]
          }
        })
      })
      .catch(err => {
        if (err?.name === 'AbortError' || controller.signal.aborted) return
        setError(err || new Error('Failed to load activity'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setActivityLoading(false)
      })
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, api])

  // SSE live updates
  const { items: streamedItems } = useShipmentActivityStream(activeId)
  const prevStreamLenRef = useRef(0)
  // Track which shipment the merge cursor belongs to. The hook clears its
  // own `items` array when shipmentId changes, but `prevStreamLenRef` lives
  // outside it — without this guard, switching shipments can leave the
  // cursor stale and silently skip the next shipment's first SSE batch when
  // its length happens to equal the previous shipment's final length.
  const streamCursorShipmentRef = useRef(null)

  useEffect(() => {
    if (!activeId || streamedItems.length === 0) return
    if (streamCursorShipmentRef.current !== activeId) {
      prevStreamLenRef.current = 0
      streamCursorShipmentRef.current = activeId
    }
    if (streamedItems.length === prevStreamLenRef.current) return
    const prevLen = prevStreamLenRef.current
    prevStreamLenRef.current = streamedItems.length

    const adapted = streamedItems.map(adaptActivityItem)
    setActivityByShipment(prev => {
      const existing = prev[activeId] || []
      const seenIds = new Set(existing.map(i => i.id))
      // Reconcile against optimistic local placeholders. The POST resolves
      // asynchronously and may lose the race to the SSE: by the time the
      // server-side INSERT broadcasts, the FE still has the row as
      // `local-<ts>` because the swap-to-persisted-id hasn't applied yet.
      // Without this pass the SSE arrival is treated as a brand-new item
      // and the bubble appears twice. We index by role+text so each SSE'd
      // item can adopt an existing local row's slot.
      const localByKey = new Map()
      for (const row of existing) {
        if (typeof row.id !== 'string' || !row.id.startsWith('local-')) continue
        if (!row.text) continue
        localByKey.set(`${row.role}|${row.text}`, row.id)
      }
      const adoptedLocalIds = new Set()
      const fresh = []
      for (const item of adapted) {
        if (seenIds.has(item.id)) continue
        const adoptKey = item.text ? `${item.role}|${item.text}` : null
        const localMatch = adoptKey ? localByKey.get(adoptKey) : null
        if (localMatch && !adoptedLocalIds.has(localMatch)) {
          adoptedLocalIds.add(localMatch)
          continue
        }
        fresh.push(item)
      }
      if (fresh.length === 0 && adoptedLocalIds.size === 0) return prev
      const merged = existing.map(row => {
        if (adoptedLocalIds.has(row.id)) {
          const key = `${row.role}|${row.text}`
          const replacement = adapted.find(a =>
            a.text && `${a.role}|${a.text}` === key && !seenIds.has(a.id)
          )
          return replacement ? { ...row, id: replacement.id } : row
        }
        return row
      })
      return { ...prev, [activeId]: [...merged, ...fresh] }
    })

    // Clear the thinking indicator only when an assistant message arrives in
    // *this* SSE batch. Checking against the full stream would spuriously
    // clear if any unrelated event (quote_card, carrier_declined, etc.) fires
    // while we're waiting — the historical AI replies would always satisfy
    // the check. Slicing past prevLen restricts to genuinely new items.
    const newlyArrived = streamedItems.slice(prevLen)
    const aiArrived = newlyArrived.some(i =>
      i.kind === 'message' && (i.role === 'assistant' || i.role === 'ai')
    )
    if (aiArrived) setThinkingShipmentId(prev => (prev === activeId ? null : prev))

    // A new quote_card / carrier_declined means freight_quotes changed; refresh
    // the shipment so the right-rail Providers section reflects the new state.
    const affectsQuotes = streamedItems.some(i =>
      i.kind === 'quote_card' || i.kind === 'carrier_declined' || i.kind === 'compare_card'
    )
    if (affectsQuotes && typeof api.getShipment === 'function') {
      api.getShipment(activeId)
        .then(res => {
          const full = res?.data
          if (!full || !full.id) return
          setShipments(prev => {
            const idx = prev.findIndex(s => s.id === full.id)
            if (idx === -1) return prev
            const next = prev.slice()
            next[idx] = { ...prev[idx], ...full }
            return next
          })
        })
        .catch(() => {})
    }
  }, [activeId, streamedItems, api])

  // For new users with no shipments AND no active sessions, auto-start chat
  // intake — but only on the first mount. Without the ref guard this re-fires
  // during the post-confirm transient where chatSessions and shipments are
  // briefly both empty (between session removal and listShipments resolving),
  // which would create a fresh empty chat that hides the just-submitted
  // shipment.
  const autoStartedRef = useRef(false)
  useEffect(() => {
    if (autoStartedRef.current) return
    if (!loading && shipments.length === 0 && chatSessions.length === 0 && !chatSessionId && !error) {
      autoStartedRef.current = true
      handleNew()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, shipments.length, chatSessions.length, chatSessionId, error])

  // Build adapted shipment objects for preview components
  const currentActivity = activityByShipment[activeId] || []
  const adaptedShipments = shipments.map(s =>
    adaptShipment(s, s.id === activeId ? currentActivity : [])
  )
  // active and chatSessionId are mutually exclusive: when a chat session is
  // open the rail must render the chat draft, never the previously focused
  // shipment. Without this guard a stale activeId (from URL pinning, async
  // races, or unmounted-effect leftovers) lets the rail fall through to the
  // old shipment's route + readyDate after the user clicks "+ New".
  const active = chatSessionId
    ? null
    : (adaptedShipments.find(s => s.id === activeId) || null)

  // Merge active chat sessions into sidebar as Draft items (before shipments).
  // Auto-name: use session title (set by backend from first user message),
  // truncated for sidebar display. Falls back to "New shipment" if untitled.
  const sessionItems = chatSessions
    .filter(s => s.status === 'active' && !s.submitted_shipment_id)
    .map(s => {
      const title = s.title || 'New shipment'
      const hasCustomTitle = title !== 'New shipment'
      const displayTitle = hasCustomTitle
        ? (title.length > 40 ? title.slice(0, 37) + '…' : title)
        : null
      return {
        id: `session:${s.id}`,
        _sessionId: s.id,
        isDraft: true,
        status: 'DRAFT',
        // Grouped-sidebar fields: a mid-chat draft is waiting on the user, so
        // it files under Action needed. No mode/route/initiator yet — the
        // intake chat hasn't pinned them down.
        group: 'action_needed',
        statusReason: (s.message_count || 0) === 1 ? '1 message' : `${s.message_count || 0} messages`,
        modeKind: null,
        originLabel: '—',
        destinationLabel: '—',
        equipmentLabel: '',
        initiator: null,
        lastActivityAt: s.updated_at || s.created_at,
        userName: displayTitle,
        // Drafts don't pulse the dot — the user is the one driving the chat,
        // there's nothing to "verify" until a quote arrives.
        needsAttention: false,
        messageCount: s.message_count || 0,
        createdAt: s.created_at,
        createdBy: '',
        route: { origin: { code: '—', name: '—' }, destination: { code: '—', name: '—' } },
        mode: '—',
        cargo: {},
        commodity: {},
        commercial: {},
        readyDate: '',
        activity: [],
        providers: [],
        timeline: [{ date: '', label: 'Draft', current: true }]
      }
    })
  const allSidebarItems = [...sessionItems, ...adaptedShipments]

  // ──────────────────────────────────────────────────────────────────
  // Handlers
  // ──────────────────────────────────────────────────────────────────

  const switchToShipment = useCallback((newId) => {
    // Handle clicking on a session item (id starts with "session:")
    if (typeof newId === 'string' && newId.startsWith('session:')) {
      const sessId = newId.replace('session:', '')
      setChatSessionId(sessId)
      setActiveId(null)
      // Reset the draft mirror so the rail starts empty until the new
      // session's GET resolves and re-publishes its state.
      setChatDraftState({})
      // Drop the pinned shipment id from the URL — otherwise the URL-sync
      // effect re-pins activeId to the previously focused shipment and the
      // rail renders that shipment's route + readyDate.
      navigate('/workspace')
      return
    }
    if (newId === activeId && !chatSessionId) return
    setChatSessionId(null)
    setChatDraftState({})
    prevStreamLenRef.current = 0
    navigate(`/workspace/${newId}`)
  }, [activeId, chatSessionId, navigate])

  const handleNew = useCallback(async () => {
    try {
      setError(null)
      const res = await api.createChatSession()
      const session = res?.data
      setChatSessionId(session.id)
      setActiveId(null)
      setChatDraftState({})
      navigate('/workspace')
      // Add to sidebar immediately so it's visible + persistent
      setChatSessions(prev => [
        { id: session.id, title: session.title || 'New shipment', status: 'active', created_at: new Date().toISOString(), message_count: 1 },
        ...prev
      ])
    } catch (err) {
      setError(err || new Error('Failed to start new shipment'))
    }
  }, [api, navigate])

  const handleShipmentCreated = useCallback((newShipmentId) => {
    const finishedSessionId = chatSessionId
    // Defer the chat→workspace swap until we have the new shipment's data
    // in memory. If we flip chatSessionId and activeId first (the old order),
    // the chat intake unmounts immediately but the rail/activity have nothing
    // to render — the user sees an empty workspace for the ~300-800ms the
    // fetches take. Fetching first means the intake stays on screen (its
    // "Submitting…" button held), and the swap happens in a single render
    // tick once shipments[] already contains the new row.
    Promise.all([
      fetchAllShipments(api).catch(() => ({ data: [] })),
      api.getShipment(newShipmentId).catch(() => null),
      api.getShipmentActivity(newShipmentId).catch(() => ({ data: [] }))
    ]).then(([listRes, detailRes, activityRes]) => {
      const rawList = Array.isArray(listRes) ? listRes : (listRes?.data || [])
      const list = Array.isArray(rawList) ? rawList : []
      const detail = detailRes?.data || detailRes || null
      const hasNewShipment = list.some(s => s.id === newShipmentId)
      const merged = detail
        ? (hasNewShipment
          ? list.map(s => (s.id === newShipmentId ? { ...s, ...detail } : s))
          : [detail, ...list])
        : list
      const activityItems = ((activityRes?.data || activityRes || [])).map(adaptActivityItem)
      // Apply data first, then swap views. React batches these so the swap
      // is atomic — no intermediate paint where active is null.
      setShipments(merged)
      setActivityByShipment(prev => ({ ...prev, [newShipmentId]: activityItems }))
      prevStreamLenRef.current = 0
      setChatSessionId(null)
      setActiveId(newShipmentId)
      if (finishedSessionId) {
        setChatSessions(prev => prev.filter(s => s.id !== finishedSessionId))
      }
      navigate(`/workspace/${newShipmentId}`, { replace: true })
    }).catch(() => {
      // Fall back to the eager swap so the user isn't stuck on the chat
      // intake forever if the hydrate fetch fails.
      prevStreamLenRef.current = 0
      setChatSessionId(null)
      setActiveId(newShipmentId)
      if (finishedSessionId) {
        setChatSessions(prev => prev.filter(s => s.id !== finishedSessionId))
      }
      navigate(`/workspace/${newShipmentId}`, { replace: true })
    })
  }, [api, navigate, chatSessionId])

  const handleRename = useCallback(async (itemId, newName) => {
    // Session items (id starts with "session:") — update title locally only
    if (typeof itemId === 'string' && itemId.startsWith('session:')) {
      const sessId = itemId.replace('session:', '')
      setChatSessions(prev => prev.map(s =>
        s.id === sessId ? { ...s, title: newName || 'New shipment' } : s
      ))
      return
    }
    try {
      await api.patchShipment(itemId, { userName: newName })
      setShipments(prev => prev.map(s =>
        s.id === itemId ? { ...s, user_name: newName } : s
      ))
    } catch (err) {
      setError(err || new Error('Failed to rename shipment'))
    }
  }, [api])

  const handleDelete = useCallback(async (itemId) => {
    if (!confirm('Delete this conversation? This cannot be undone.')) return

    // Session item
    if (typeof itemId === 'string' && itemId.startsWith('session:')) {
      const sessId = itemId.replace('session:', '')
      try {
        await api.deleteChatSession(sessId)
        setChatSessions(prev => prev.filter(s => s.id !== sessId))
        if (chatSessionId === sessId) {
          setChatSessionId(null)
          // Select first shipment or first remaining session
          if (shipments.length > 0) {
            navigate(`/workspace/${shipments[0].id}`, { replace: true })
          }
        }
      } catch (err) {
        setError(err || new Error('Failed to delete conversation'))
      }
      return
    }

    // Shipment item
    try {
      await api.deleteShipment(itemId)
      setShipments(prev => prev.filter(s => s.id !== itemId))
      if (activeId === itemId) {
        setActiveId(null)
        const remaining = shipments.filter(s => s.id !== itemId)
        if (remaining.length > 0) {
          navigate(`/workspace/${remaining[0].id}`, { replace: true })
        }
      }
    } catch (err) {
      setError(err || new Error('Failed to delete shipment'))
    }
  }, [api, chatSessionId, activeId, shipments, navigate])

  const handleFieldUpdate = useCallback(async (section, fieldKey, newValue, opts = {}) => {
    if (!activeId) return
    const activeRaw = shipments.find(s => s.id === activeId)
    const isTrucking = activeRaw?.quote_type === 'domestic_trucking'
    const fields = {}
    if (section === 'route') {
      // PATCH /shipments keys are camelCase — shipmentService.update maps
      // camelCase to columns and ignores anything else. These four rows used to
      // send snake_case, so every edit in this section was dropped silently:
      // the rail showed the new value, the row reloaded, and the old one came
      // back with no error anywhere.
      //
      // Origin/Destination write to the columns the rail READS for this kind
      // of load: trucking shows pickup_address / destination_address, ocean
      // shows origin_port / destination_port. An ocean edit also clears the
      // LOCODE, which otherwise keeps winning at dispatch — the RFQ would go
      // out to the old port while the screen shows the new one. A cleared
      // LOCODE is re-resolved from the port text when quotes are requested
      // (backfillOceanLocodesIfMissing).
      if (fieldKey === 'originName') {
        if (isTrucking) fields.pickupAddress = newValue
        else { fields.originPort = newValue; fields.originLocode = null }
      } else if (fieldKey === 'destName') {
        if (isTrucking) fields.destinationAddress = newValue
        else { fields.destinationPort = newValue; fields.destinationLocode = null }
      } else if (fieldKey === 'mode') {
        // Only trucking has a column behind this row: FTL/LTL (shipment_mode),
        // which routes the RFQ. An ocean load's "Ocean FCL" / "Air" label has
        // no column — container_type holds '40HC' and the like — so the rail
        // shows that row read-only and nothing is written here.
        if (!isTrucking) return
        const picked = String(newValue || '').replace(/^Truck\s+/i, '').toUpperCase()
        fields.shipmentMode = picked === 'FTL' || picked === 'LTL' ? picked : null
      }
      else if (fieldKey === 'readyDate') fields.cargoReadyDate = newValue
    } else if (section === 'cargo') {
      if (fieldKey === 'totalWeight') {
        // Weight is split across two columns: total_weight (kg, ocean/air/LCL)
        // vs total_weight_lbs (trucking). Route the patch to the column that
        // matches this shipment's quote_type — otherwise the value lands in
        // the wrong column and the rail keeps showing "not set".
        fields[isTrucking ? 'totalWeightLbs' : 'totalWeight'] = newValue
      } else if (fieldKey === 'palletCount') {
        // Rail edits the scalar columns added in v2_039 — `palletCount` and
        // `dims` are FE-only keys; map to the backend's camelCase names.
        const n = newValue === '' || newValue == null ? null : Number(newValue)
        fields.palletsCount = Number.isFinite(n) ? n : null
      } else if (fieldKey === 'dims') {
        const s = typeof newValue === 'string' ? newValue.trim() : ''
        fields.dimensionsText = s || null
      } else {
        fields[fieldKey] = newValue
      }
    } else if (section === 'commodity') {
      if (fieldKey === 'description') fields.commodity = newValue
    } else if (section === 'commercial') {
      if (fieldKey === 'incoterm') fields.incoterm = newValue
    }

    if (opts.notify) fields.notify = true

    // Build the snake_case overlay so the rail reflects the edit instantly,
    // before the PATCH round-trip completes. Without this, the row stored in
    // state is snake_case but `fields` is camelCase (PATCH contract), so a
    // direct merge wouldn't update what the adapter reads — the user had to
    // refresh to see their save.
    const PATCH_TO_RAW = {
      originPort: 'origin_port',
      destinationPort: 'destination_port',
      originLocode: 'origin_locode',
      destinationLocode: 'destination_locode',
      pickupAddress: 'pickup_address',
      destinationAddress: 'destination_address',
      containerType: 'container_type',
      cargoReadyDate: 'cargo_ready_date',
      commodity: 'commodity',
      incoterm: 'incoterm',
      totalWeight: 'total_weight',
      totalWeightLbs: 'total_weight_lbs',
      palletsCount: 'pallets_count',
      dimensionsText: 'dimensions_text',
      shipmentMode: 'shipment_mode'
    }
    const optimistic = {}
    for (const [k, v] of Object.entries(fields)) {
      if (k === 'notify') continue
      const rawKey = PATCH_TO_RAW[k] || k
      optimistic[rawKey] = v
    }
    const prevRow = shipments.find(s => s.id === activeId)
    if (Object.keys(optimistic).length > 0) {
      setShipments(prev => prev.map(s =>
        s.id === activeId ? { ...s, ...optimistic } : s
      ))
    }

    try {
      await api.patchShipment(activeId, fields)
      // Re-fetch activity to pick up system event from notify
      if (opts.notify) {
        const actRes = await api.getShipmentActivity(activeId).catch(() => null)
        if (actRes) {
          const items = (actRes?.data || actRes || []).map(adaptActivityItem)
          setActivityByShipment(prev => ({
            ...prev,
            [activeId]: mergeActivity(prev[activeId], items)
          }))
        }
      }
      // Re-fetch the shipment so the rail reflects authoritative server state
      // (updated_at, derived fields, etc.) on top of the optimistic overlay.
      if (typeof api.getShipment === 'function') {
        const fresh = await api.getShipment(activeId).catch(() => null)
        const full = fresh?.data
        if (full && full.id) {
          setShipments(prev => {
            const idx = prev.findIndex(s => s.id === full.id)
            if (idx === -1) return prev
            const next = prev.slice()
            next[idx] = { ...prev[idx], ...full }
            return next
          })
        }
      }
    } catch (err) {
      // PATCH failed — roll back the optimistic overlay so the rail doesn't
      // show a value the backend never accepted.
      if (prevRow) {
        setShipments(prev => prev.map(s => s.id === activeId ? prevRow : s))
      }
      setError(err || new Error('Failed to update field'))
    }
  }, [activeId, api, shipments])

  // Posts a customer message to the active shipment's chat. Centralised so
  // both the input bar and the provider-picker NL submit reuse the same
  // optimistic flow (local placeholder → swap id on POST resolve → SSE dedup
  // by id). Returns true on success, false on failure.
  //
  // When the backend recognises a "send to <provider>" intent and dispatches
  // an RFQ, the response carries `quotesDispatched`. In that case we mirror
  // the manual handleSendQuotes flow — refresh shipment+activity so the rail
  // sees the new providers and the picker auto-hides, and surface the
  // "RFQs sent" confirmation panel so the success state is identical to a
  // checkbox-driven send.
  const sendMessageToChat = useCallback(async (text) => {
    if (!text || !activeId) return false
    if (isSendingRef.current) return false
    isSendingRef.current = true
    setIsSending(true)
    setThinkingShipmentId(activeId)

    const localId = `local-${Date.now()}`
    const optimisticMsg = {
      id: localId,
      kind: 'message',
      role: 'customer',
      author: 'You',
      text
    }
    const selectionItemBeforeSend = (activityByShipment[activeId] || [])
      .find(a => a.kind === 'provider_selection' && !a.dismissed)
    setActivityByShipment(prev => ({
      ...prev,
      [activeId]: [...(prev[activeId] || []), optimisticMsg]
    }))

    try {
      const selectedQuoteIds = shareSelectionRef.current[activeId] || []
      const res = await api.postShipmentMessage(activeId, text, 'user', { selectedQuoteIds })
      const persistedId = res?.data?.id
      if (persistedId) {
        setActivityByShipment(prev => ({
          ...prev,
          [activeId]: (prev[activeId] || []).map(a =>
            a.id === localId ? { ...a, id: persistedId } : a
          )
        }))
      }
      const dispatched = res?.data?.quotesDispatched
      const pickerDismissed = res?.data?.pickerDismissed === true
      if (dispatched && dispatched.providerCount > 0) {
        const [actRes, shipRes] = await Promise.all([
          api.getShipmentActivity(activeId).catch(() => null),
          api.getShipment(activeId).catch(() => null)
        ])
        if (actRes) {
          const items = (actRes?.data || actRes || []).map(adaptActivityItem)
          setActivityByShipment(prev => ({
            ...prev,
            [activeId]: mergeActivity(prev[activeId], items)
          }))
        }
        const detail = shipRes?.data || shipRes || null
        if (detail) {
          setShipments(prev => prev.map(s =>
            s.id === activeId ? { ...s, ...detail } : s
          ))
        }
        setQuoteSendConfirmations(prev => ({
          ...prev,
          [activeId]: {
            providerCount: Number(dispatched.providerCount || 0),
            providerIds: Array.isArray(dispatched.providerIds) ? dispatched.providerIds : [],
            selectionItem: selectionItemBeforeSend || null,
            sentAt: Date.now()
          }
        }))
      } else if (pickerDismissed) {
        // Backend marked the open picker dismissed in response to a negative
        // intent ("no thanks", "ya está bien"). The UPDATE doesn't broadcast
        // via SSE, so refresh activity to pick up the dismissed_at flag and
        // hide the picker locally.
        const actRes = await api.getShipmentActivity(activeId).catch(() => null)
        if (actRes) {
          const items = (actRes?.data || actRes || []).map(adaptActivityItem)
          setActivityByShipment(prev => ({
            ...prev,
            [activeId]: mergeActivity(prev[activeId], items)
          }))
        }
      }
      return true
    } catch (err) {
      setActivityByShipment(prev => ({
        ...prev,
        [activeId]: (prev[activeId] || []).filter(a => a.id !== localId)
      }))
      setError(err || new Error('Failed to send message'))
      setThinkingShipmentId(prev => (prev === activeId ? null : prev))
      return false
    } finally {
      isSendingRef.current = false
      setIsSending(false)
    }
  }, [activeId, api, activityByShipment])

  const handleSend = useCallback(async () => {
    if (!input.trim()) return
    const text = input.trim()
    setInput('')
    await sendMessageToChat(text)
  }, [input, sendMessageToChat])

  // Chat-driven share (MVP): dispatch curates with the table checkboxes and hits
  // "Share" → we hand off to Marq in the chat with the selected quotes. Marq asks
  // who to send to and sends a text email (Company + price, no carrier contact,
  // no follow-up) — that side is backend (Richard). Here we just kick off the chat.
  const handleShareQuotes = useCallback(async (payload) => {
    const quotes = payload?.selected || []
    if (quotes.length === 0) return
    const quoteIds = quotes.map(q => q.quoteId).filter(Boolean)
    if (quoteIds.length === 0 || !activeId) return
    try {
      await api.shareIntent(activeId, quoteIds)
      // Pending_share + the two opening chat turns are written server-side.
      // Refresh activity so the user sees them immediately.
      const actRes = await api.getShipmentActivity(activeId).catch(() => null)
      if (actRes) {
        const items = (actRes?.data || actRes || []).map(adaptActivityItem)
        setActivityByShipment(prev => ({
          ...prev,
          [activeId]: mergeActivity(prev[activeId], items)
        }))
      }
    } catch (err) {
      setError(err || new Error('Failed to start share'))
    }
  }, [activeId, api])

  // Current comparison-table curation, keyed by shipment, reported by
  // ProviderComparisonCard. sendMessageToChat reads it so a typed share
  // ("share quotes to <email>") sends exactly what's ticked — the single
  // source of truth shared with the Share button. Keyed by shipment so a
  // selection on one shipment never leaks into a typed share on another.
  const shareSelectionRef = useRef({})
  const handleShareSelectionChange = useCallback((quoteIds) => {
    if (!activeId) return
    shareSelectionRef.current[activeId] = Array.isArray(quoteIds) ? quoteIds : []
  }, [activeId])

  const handleSendQuotes = useCallback(async (_selectionItemId, providerIds) => {
    if (!activeId) return
    const selectionItem = (activityByShipment[activeId] || [])
      .find(a => a.id === _selectionItemId && a.kind === 'provider_selection')
    setSendingQuoteSelectionId(_selectionItemId)
    try {
      const result = await api.requestQuotes(activeId, providerIds)
      const requestData = result?.data || result || {}
      const providerCount = Number(requestData.providersContacted || providerIds?.length || 0)
      // Re-fetch activity and shipment detail. The list endpoint may not have
      // quote rows, so the detail refresh keeps the provider rail from looking
      // empty immediately after RFQs are sent.
      const [res, shipmentRes] = await Promise.all([
        api.getShipmentActivity(activeId),
        api.getShipment(activeId).catch(() => null)
      ])
      const items = (res?.data || res || []).map(adaptActivityItem)
      setActivityByShipment(prev => ({
        ...prev,
        [activeId]: mergeActivity(prev[activeId], items)
      }))
      const detail = shipmentRes?.data || shipmentRes || null
      if (detail) {
        setShipments(prev => prev.map(s =>
          s.id === activeId ? { ...s, ...detail } : s
        ))
      }
      setQuoteSendConfirmations(prev => ({
        ...prev,
        [activeId]: {
          providerCount,
          providerIds: providerIds || [],
          selectionItem,
          sentAt: Date.now()
        }
      }))
    } catch (err) {
      setError(err || new Error('Failed to send quote requests'))
    } finally {
      setSendingQuoteSelectionId(null)
    }
  }, [activeId, activityByShipment, api])

  const refreshShipmentAndActivity = useCallback(async (shipmentId) => {
    const [activityRes, shipmentRes] = await Promise.all([
      api.getShipmentActivity(shipmentId),
      api.getShipment(shipmentId).catch(() => null)
    ])
    const detail = shipmentRes?.data || shipmentRes || null
    const rawItems = (activityRes?.data || activityRes || []).map(adaptActivityItem)
    const items = mergeShipmentQuoteStateIntoActivity(rawItems, detail)
    setActivityByShipment(prev => ({
      ...prev,
      [shipmentId]: mergeActivity(prev[shipmentId], items)
    }))

    if (detail) {
      setShipments(prev => prev.map(s =>
        s.id === shipmentId ? { ...s, ...detail } : s
      ))
    }
  }, [api])

  const applyQuoteSelectionLocally = useCallback((shipmentId, quote, selectionReason) => {
    setActivityByShipment(prev => ({
      ...prev,
      [shipmentId]: (prev[shipmentId] || []).map(item => {
        if (item.kind !== 'quote' || !item.quoteId) return item
        const selected = item.quoteId === quote.quoteId
        return {
          ...item,
          status: selected ? 'SELECTED' : 'REJECTED',
          selected,
          selectionReason: selected ? selectionReason : item.selectionReason
        }
      })
    }))

    setShipments(prev => prev.map(s => {
      if (s.id !== shipmentId) return s
      const quotes = Array.isArray(s.quotes)
        ? s.quotes.map(q => {
          const id = quoteIdOf(q)
          if (!id) return q
          return {
            ...q,
            status: id === quote.quoteId ? 'SELECTED' : 'REJECTED',
            selected: id === quote.quoteId
          }
        })
        : s.quotes
      return {
        ...s,
        status: 'QUOTE_CONFIRMED',
        selected_quote_id: quote.quoteId,
        quotes
      }
    }))
  }, [])

  const [submittingBookingChoice, setSubmittingBookingChoice] = useState(false)

  const handleConfirmBookingIntent = useCallback(async (choiceItem, method) => {
    if (!activeId || !choiceItem?.quoteId) return
    setSubmittingBookingChoice(true)
    setError(null)
    // Surface the same "Marq is thinking…" indicator we use for chat
    // turns so the user sees Marq is working between submit and the
    // terminal "Your part is done" card. Cleared in finally — the new
    // booking_sent / booking_handed_off activity replaces the choice
    // card visually.
    setThinkingShipmentId(activeId)
    try {
      await api.confirmBookingIntent(activeId, {
        quoteId: choiceItem.quoteId,
        method
      })
      await refreshShipmentAndActivity(activeId).catch(() => null)
    } catch (err) {
      setError(err || new Error('Failed to confirm booking intent'))
    } finally {
      setSubmittingBookingChoice(false)
      setThinkingShipmentId(prev => (prev === activeId ? null : prev))
    }
  }, [activeId, api, refreshShipmentAndActivity])

  const handleAcceptQuote = useCallback(async (quote) => {
    if (!activeId || !quote?.quoteId) return

    setAcceptingQuoteId(quote.quoteId)
    setError(null)
    // Show "Marq is thinking…" while the backend marks SELECTED and
    // appends the booking_choice card — gives the user a beat to read
    // that Marq is composing the next step rather than a blank gap.
    setThinkingShipmentId(activeId)
    try {
      const comparisonRes = await api.createComparison(activeId)
      const comparisonData = comparisonRes?.data || comparisonRes || {}
      const comparisonId = comparisonData?.comparison?.id || comparisonData?.comparisonId || comparisonData?.id
      if (!comparisonId) {
        throw new Error('Unable to accept quote because no comparison was created.')
      }

      const reason = comparisonData.reasoning || comparisonData.reason || `Accepted ${quote.provider || 'provider'} quote from the workspace.`
      await api.selectQuote(activeId, quote.quoteId, {
        comparisonId,
        reason
      })
      applyQuoteSelectionLocally(activeId, quote, reason)
      setQuoteAcceptConfirmation({
        shipmentId: activeId,
        quoteId: quote.quoteId,
        provider: quote.provider || null
      })
      await refreshShipmentAndActivity(activeId).catch(() => null)
    } catch (err) {
      setError(err || new Error('Failed to accept quote'))
    } finally {
      setAcceptingQuoteId(null)
      setThinkingShipmentId(prev => (prev === activeId ? null : prev))
    }
  }, [activeId, api, applyQuoteSelectionLocally, refreshShipmentAndActivity])

  const handleViewQuotePdf = useCallback(async (quote) => {
    if (!activeId || !quote?.quoteId) return

    setViewingPdfQuoteId(quote.quoteId)
    setError(null)
    try {
      const blob = await api.getQuotePdfBlob(activeId, quote.quoteId)
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank', 'noopener,noreferrer')
      window.setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch (err) {
      setError(err || new Error('Failed to open quote PDF'))
    } finally {
      setViewingPdfQuoteId(null)
    }
  }, [activeId, api])

  const handleDismissSelection = useCallback(async (selectionItemId) => {
    if (!activeId) return
    try {
      await api.dismissActivityItem(activeId, selectionItemId)
      // Optimistic update
      setActivityByShipment(prev => ({
        ...prev,
        [activeId]: (prev[activeId] || []).map(a =>
          a.id === selectionItemId ? { ...a, dismissed: true } : a
        )
      }))
    } catch (err) {
      setError(err || new Error('Failed to dismiss selection'))
    }
  }, [activeId, api])

  // Manual-add: persist the new provider via POST /api/v2/providers, then
  // append a candidate row for it on the active provider_selection activity
  // item so the picker shows the new provider as a selectable option.
  //
  // The preview-side ProviderSelectionPanel pre-selects `_formPayload`-bearing
  // candidates locally with a `manual-<ts>` id BEFORE the parent commits;
  // the persisted candidate we append here carries the real backend provider
  // id, which is what the Send button will dispatch to requestQuotes.
  const handleAddManualProvider = useCallback(async (itemId, candidate) => {
    if (!activeId || !candidate?._formPayload) return
    try {
      const res = await api.createProvider(candidate._formPayload)
      const created = res?.data || res
      if (!created?.id) {
        throw new Error('Provider create returned no id')
      }
      const persistedCandidate = {
        providerId: created.id,
        name: created.name || candidate.name,
        score: 0,
        suggested: false,
        manuallyAdded: true,
        rationale: 'Added manually',
        truckingMode: created.truckingMode ?? created.trucking_mode ?? candidate.truckingMode ?? null
      }
      setActivityByShipment(prev => {
        const items = prev[activeId] || []
        const next = items.map(a => {
          if (a.id !== itemId || a.kind !== 'provider_selection') return a
          // Avoid duplicates if the same provider gets added twice.
          const alreadyPresent = (a.candidates || []).some(c => c.providerId === created.id)
          if (alreadyPresent) return a
          return {
            ...a,
            candidates: [...(a.candidates || []), persistedCandidate]
          }
        })
        return { ...prev, [activeId]: next }
      })
    } catch (err) {
      setError(err || new Error('Failed to add provider'))
    }
  }, [activeId, api])

  const handleSendMoreProviders = useCallback(async () => {
    if (!activeId) return
    const confirmation = quoteSendConfirmations[activeId]
    setQuoteSendConfirmations(prev => {
      const next = { ...prev }
      delete next[activeId]
      return next
    })
    // Pull a fresh candidate list — the stale selectionItem captured pre-
    // dispatch still contains the providers we just contacted. The backend
    // suggestion endpoint already excludes them, so this hand-off gives the
    // picker an up-to-date set (often empty when everyone's been contacted).
    let freshPayload = null
    if (typeof api.suggestProviders === 'function') {
      try {
        const res = await api.suggestProviders(activeId)
        freshPayload = res?.data || res || null
      } catch {
        // Best-effort — falls back to the stale selectionItem below.
      }
    }
    const baseItem = confirmation?.selectionItem || null
    const reopenedItem = baseItem || {
      id: `local-picker-${Date.now()}`,
      kind: 'provider_selection',
      candidates: [],
      suggestedCount: freshPayload?.recommendedCount || 3,
      tenantMedianCount: freshPayload?.tenantMedianCount ?? null,
      quoteType: freshPayload?.quoteType || null
    }
    const refreshedItem = {
      ...reopenedItem,
      dismissed: false,
      reopened: true,
      candidates: Array.isArray(freshPayload?.candidates)
        ? freshPayload.candidates
        : reopenedItem.candidates || [],
      suggestedCount: freshPayload?.recommendedCount || reopenedItem.suggestedCount || 3,
      tenantMedianCount: freshPayload?.tenantMedianCount ?? reopenedItem.tenantMedianCount ?? null,
      quoteType: freshPayload?.quoteType || reopenedItem.quoteType || null
    }
    setActivityByShipment(prev => {
      const items = prev[activeId] || []
      let found = false
      const nextItems = items.map(item => {
        if (item.id !== refreshedItem.id) return item
        found = true
        return refreshedItem
      })
      if (!found) nextItems.push(refreshedItem)
      return { ...prev, [activeId]: nextItems }
    })
  }, [activeId, api, quoteSendConfirmations])

  const handleKeepChattingAfterSend = useCallback(() => {
    if (!activeId) return
    const confirmation = quoteSendConfirmations[activeId]
    const selectionId = confirmation?.selectionItem?.id

    // Clear the "RFQs sent" panel locally — the provider_selection row stays
    // in the activity log (already auto-hidden by the dismissed_at SQL CASE
    // once a quote_request exists for the shipment). No backend dismiss call:
    // the dismiss endpoint errors on items that are already system-suppressed,
    // and the user's intent here is "let me type", not "delete this card".
    setQuoteSendConfirmations(prev => {
      const next = { ...prev }
      delete next[activeId]
      return next
    })

    if (selectionId) {
      setActivityByShipment(prev => ({
        ...prev,
        [activeId]: (prev[activeId] || []).map(a =>
          a.id === selectionId ? { ...a, dismissed: true } : a
        )
      }))
    }
  }, [activeId, quoteSendConfirmations])

  // Auto-dismiss the "Quote requests sent" banner once a carrier reply has
  // landed for the active shipment. The banner's purpose is "we're waiting
  // for replies — they'll appear here" — once a reply is in the chat (quote,
  // decline, follow-up question) or a non-PENDING quote row exists on the
  // shipment, the message is stale and the banner just shoves the chat
  // input down for no reason.
  useEffect(() => {
    if (!activeId) return
    if (!quoteSendConfirmations[activeId]) return
    const items = activityByShipment[activeId] || []
    const hasActivityResponse = items.some(a =>
      a.kind === 'quote'
      || a.kind === 'compare'
      || a.kind === 'carrier_declined'
      || a.kind === 'carrier_question'
    )
    const shipmentRow = shipments.find(s => s.id === activeId)
    const hasQuoteResponse = Array.isArray(shipmentRow?.quotes)
      && shipmentRow.quotes.some(q => q.status && q.status !== 'PENDING' && q.status !== 'CANCELLED')
    if (!hasActivityResponse && !hasQuoteResponse) return
    setQuoteSendConfirmations(prev => {
      if (!prev[activeId]) return prev
      const next = { ...prev }
      delete next[activeId]
      return next
    })
  }, [activeId, activityByShipment, shipments, quoteSendConfirmations])

  // ──────────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="ws-app">
        <div style={{ padding: 40, opacity: 0.6 }}>Loading workspace…</div>
      </div>
    )
  }

  if (error && shipments.length === 0) {
    return (
      <div className="ws-app">
        <div style={{ padding: 24 }}>
          <div style={{
            padding: 16, background: 'rgba(139,58,58,0.08)', color: '#8B3A3A',
            border: '1px solid #8B3A3A', borderRadius: 6
          }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Failed to load shipments</div>
            <div style={{ fontSize: 13, marginBottom: 12 }}>{friendlyErrorMessage(error)}</div>
            <button
              onClick={() => { setError(null); setLoading(true); fetchAllShipments(api).then(res => { setShipments(res?.data || []); }).catch(err => setError(err)).finally(() => setLoading(false)); }}
              style={{
                padding: '6px 16px', background: '#23201C', color: '#F3F2ED',
                border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13
              }}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    )
  }

  const hasQuoteWorkflowStarted = active?.status === 'DECIDE' || (active?.providers?.length || 0) > 0
  const pendingSelection = active?.activity.find(
    a => a.kind === 'provider_selection' && !a.dismissed
      && (!hasQuoteWorkflowStarted || a.reopened)
  )
  const quoteSendConfirmation = activeId ? quoteSendConfirmations[activeId] : null
  // Chat is "closed" once the user has submitted a booking_choice — either
  // path (Marq sends or user direct) ends in a terminal activity card.
  // We drop the input + footer panels so the conversation can't continue.
  const chatClosed = !!active?.activity?.some(
    a => a.kind === 'booking_sent' || a.kind === 'booking_handed_off'
  )

  return (
    <div className="ws-app ws-app--embedded">
      {error && (
        <div className="ws-error-toast" role="alert" aria-live="polite">
          <span className="ws-error-toast-text">{friendlyErrorMessage(error)}</span>
          <button
            className="ws-error-toast-action"
            onClick={() => {
              setError(null)
              setLoading(true)
              fetchAllShipments(api)
                .then(res => { setShipments(res?.data || []) })
                .catch(err => setError(err))
                .finally(() => setLoading(false))
            }}
          >
            Retry
          </button>
          <button
            className="ws-error-toast-dismiss"
            onClick={() => setError(null)}
            aria-label="Dismiss error"
          >✕</button>
        </div>
      )}
      <QuoteAcceptedBanner
        confirmation={quoteAcceptConfirmation?.shipmentId === activeId ? quoteAcceptConfirmation : null}
        onDismiss={() => setQuoteAcceptConfirmation(null)}
      />

      <div className="ws-grid">
        <Sidebar
          shipments={allSidebarItems}
          activeId={chatSessionId ? `session:${chatSessionId}` : activeId}
          onSelect={switchToShipment}
          onNew={handleNew}
          onRename={handleRename}
          onDelete={handleDelete}
          filter={filter}
          onFilter={setFilter}
        />

        <div className="ws-main">
          {chatSessionId ? (
            <WorkspaceChatIntake
              // Key by sessionId so the component remounts on session switch.
              // Without this, internal state from the previous session leaks
              // through onStateChange until the new session's GET resolves,
              // and the rail keeps rendering the previous session's data.
              key={chatSessionId}
              sessionId={chatSessionId}
              api={api}
              onShipmentCreated={handleShipmentCreated}
              onCancel={() => { setChatSessionId(null); setChatDraftState({}); if (shipments.length > 0) setActiveId(shipments[0].id); }}
              onStateChange={setChatDraftState}
            />
          ) : active ? (
            <>
              <Header
                key={(active.userName || '') + active.id}
                shipment={active}
                allShipments={adaptedShipments}
                onRename={handleRename}
              />
              {activityLoading && currentActivity.length === 0 ? (
                <div className="ws-activity-skeleton" aria-busy="true" aria-label="Loading activity">
                  {[0, 1, 2].map(i => (
                    <div key={i} className="ws-activity-skeleton-row">
                      <div className="ws-activity-skeleton-avatar" />
                      <div className="ws-activity-skeleton-bubble">
                        <div className="ws-activity-skeleton-line is-short" />
                        <div className="ws-activity-skeleton-line is-full" />
                        <div className="ws-activity-skeleton-line is-mid" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
              <ActivityLog
                shipment={active}
                onSendQuotes={handleSendQuotes}
                onAddManualProvider={handleAddManualProvider}
                onAcceptQuote={handleAcceptQuote}
                onShare={handleShareQuotes}
                onShareSelectionChange={handleShareSelectionChange}
                onViewQuotePdf={handleViewQuotePdf}
                onConfirmBookingIntent={handleConfirmBookingIntent}
                submittingBookingChoice={submittingBookingChoice}
                acceptingQuoteId={acceptingQuoteId}
                viewingPdfQuoteId={viewingPdfQuoteId}
                isStreaming={thinkingShipmentId === activeId}
                showPresence={!pendingSelection && !quoteSendConfirmation && !chatClosed}
                currentThought={thinkingShipmentId === activeId ? 'Marq is thinking…' : null}
                typingTextById={{}}
              />
              {chatClosed ? null : pendingSelection ? (
                <ProviderSelectionPanel
                  variant="footer"
                  item={pendingSelection}
                  onSend={ids => handleSendQuotes(pendingSelection.id, ids)}
                  onAddManual={handleAddManualProvider}
                  onMessage={sendMessageToChat}
                  onDismiss={() => handleDismissSelection(pendingSelection.id)}
                  sending={sendingQuoteSelectionId === pendingSelection.id}
                />
              ) : (
                <>
                  {quoteSendConfirmation && (
                    <QuoteRequestSentPanel
                      confirmation={quoteSendConfirmation}
                      onSendMore={handleSendMoreProviders}
                    />
                  )}
                  <InputBar
                    value={input}
                    onChange={(v) => {
                      setInput(v);
                      // Typing implicitly dismisses the success banner —
                      // matches the old "Keep chatting" intent without the
                      // extra click. Banner stays as long as the input is
                      // empty so a one-glance read is still possible.
                      if (v && quoteSendConfirmation) handleKeepChattingAfterSend();
                    }}
                    onSend={handleSend}
                    disabled={isSending}
                  />
                </>
              )}
            </>
          ) : (
            <div style={{ padding: 40, color: 'var(--ws-text-muted, #8F8F85)' }}>
              Select a shipment from the sidebar.
            </div>
          )}
        </div>

        {(active || chatSessionId) && (
          <div className={`ws-detail ${railOpen ? 'is-open' : 'is-collapsed'}`}>
            {railOpen ? (
              <>
                <button
                  type="button"
                  className="ws-detail-collapse"
                  onClick={() => setRailOpen(false)}
                  title="Collapse detail"
                  aria-label="Collapse detail panel"
                >
                  <ChevronRight size={16} strokeWidth={2} />
                </button>
                <ShipmentRail
                  shipment={active || buildChatDraftShipment(chatDraftState)}
                  onFieldUpdate={active ? handleFieldUpdate : () => {}}
                />
              </>
            ) : (
              <button
                type="button"
                className="ws-detail-tab"
                onClick={() => setRailOpen(true)}
                title="Open detail"
                aria-label="Open detail panel"
              >
                <ChevronLeft size={14} strokeWidth={2} />
                <span className="ws-detail-tab-text">Detail</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
